import React from "react";
import ReactDOM from "react-dom/client";
import "./tailwind.generated.css";
import App from "./App";
import { Provider } from "react-redux";
import store from "./store";
import { ThemeProvider } from "./context/ThemeContext";
import { toErrorWithMessage } from "./utils/error";

const normalizeDevServerWebSocket = () => {
  if (typeof window === "undefined" || typeof window.WebSocket !== "function") {
    return;
  }

  const NativeWebSocket = window.WebSocket;

  if (NativeWebSocket.__isClinicaPatched) {
    return;
  }

  const rewriteUrl = (url) => {
    if (typeof url !== "string") {
      return url;
    }

    try {
      const parsedUrl = new URL(url, window.location.href);
      const isSameHost = parsedUrl.hostname === window.location.hostname;
      const isDevSocketPath = parsedUrl.pathname === "/ws";
      const isDevServerPort = parsedUrl.port === "3000";

      if (isSameHost && isDevSocketPath && isDevServerPort) {
        const desiredPort = window.location.port;

        if (desiredPort) {
          parsedUrl.port = desiredPort;
        } else if (parsedUrl.protocol === "wss:") {
          parsedUrl.port = "";
        } else if (parsedUrl.protocol === "ws:") {
          parsedUrl.port = "";
        }

        return parsedUrl.toString();
      }

      return url;
    } catch (error) {
      console.warn("Failed to normalize WebSocket URL", error);
      return url;
    }
  };

  function WebSocketWrapper(url, protocols) {
    const normalizedUrl = rewriteUrl(url);
    if (protocols !== undefined) {
      return new NativeWebSocket(normalizedUrl, protocols);
    }
    return new NativeWebSocket(normalizedUrl);
  }

  WebSocketWrapper.prototype = NativeWebSocket.prototype;
  Object.setPrototypeOf(WebSocketWrapper, NativeWebSocket);
  ["CONNECTING", "OPEN", "CLOSING", "CLOSED"].forEach((stateKey) => {
    Object.defineProperty(WebSocketWrapper, stateKey, {
      configurable: true,
      enumerable: true,
      get() {
        return NativeWebSocket[stateKey];
      },
    });
  });
  WebSocketWrapper.__isClinicaPatched = true;

  window.WebSocket = WebSocketWrapper;
};

normalizeDevServerWebSocket();

if (typeof window !== "undefined" && !window.__clinicaUnhandledRejectionHandler) {
  const handleUnhandledRejection = (event) => {
    const normalizedError = toErrorWithMessage(
      event?.reason,
      "An unexpected application error occurred."
    );

    console.error("Unhandled promise rejection captured:", normalizedError);

    if (event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }

    if (event && typeof event.stopPropagation === "function") {
      event.stopPropagation();
    }

    if (event && typeof event.stopImmediatePropagation === "function") {
      event.stopImmediatePropagation();
    }

    return false;
  };

  window.addEventListener("unhandledrejection", handleUnhandledRejection, true);
  window.__clinicaUnhandledRejectionHandler = handleUnhandledRejection;
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <Provider store={store}>
    <ThemeProvider>
      <App />
    </ThemeProvider>
  </Provider>
);
